import { useState } from "react";
import type { Rec, Finding, PicRow, TrendPoint, WorkItem } from "@shared/types.js";
import { fnum } from "../lib/format.js";
import {
  recordExport, findingExport, summaryExport, picExport, trendExport, worklistExport,
  downloadCsv, copyForExcel, toDelim, type Sheet,
} from "../lib/exportData.js";

type Kind = "records" | "worklist" | "findings" | "summary" | "pic" | "trend";

export function ExportPanel({
  rows, findings, pic, trend, worklist, onToast,
}: {
  rows: Rec[]; findings: Finding[]; pic: PicRow[]; trend: TrendPoint[];
  worklist: WorkItem[];
  onToast: (msg: string) => void;
}) {
  const [preview, setPreview] = useState("");

  const build = (kind: Kind): Sheet => {
    switch (kind) {
      case "records": return recordExport(rows);
      case "worklist": return worklistExport(worklist);
      case "findings": return findingExport(findings);
      case "summary": return summaryExport(rows);
      case "pic": return picExport(pic);
      case "trend": return trendExport(trend);
    }
  };

  const doCsv = (kind: Kind) => {
    const sheet = build(kind);
    downloadCsv(sheet);
    setPreview(toDelim(sheet, ","));
    onToast(`Downloaded ${fnum(sheet.rows.length)} rows`);
  };

  const doCopy = async (kind: Kind) => {
    const sheet = build(kind);
    const n = await copyForExcel(sheet);
    setPreview(toDelim(sheet, "\t"));
    onToast(`Copied ${fnum(n)} rows — paste into Excel`);
  };

  /* Each export wears the icon of the panel it came from, so the card and the
     sidebar item it mirrors are recognisable as the same thing. */
  const ICON: Record<Kind, string> = {
    records: "bi-table",
    worklist: "bi-list-check",
    findings: "bi-shield-check",
    summary: "bi-grid-3x3-gap",
    pic: "bi-people",
    trend: "bi-graph-up-arrow",
  };

  const cards: { kind: Kind; title: string; body: string }[] = [
    {
      kind: "records", title: "Filtered records",
      body: `${fnum(rows.length)} rows — all identity, status, PIC and date fields, plus the derived status columns used by this dashboard.`,
    },
    {
      kind: "worklist", title: "Worklist",
      body: `${fnum(worklist.length)} outstanding actions, most urgent first — ready to hand out or print as a to-do list.`,
    },
    {
      kind: "findings", title: "Crosscheck findings",
      body: `${fnum(findings.length)} findings — severity, check name, the plain-English issue, and the exact sheet + Excel row so the record can be opened in the Masterfile.`,
    },
    {
      kind: "summary", title: "Summary by component & category",
      body: "The KPI table as a grid: records, received, priced, not received, estimated, outstanding, crosschecked and exported, per component and per category A–E.",
    },
    {
      kind: "pic", title: "Workload by PIC",
      body: `${fnum(pic.length)} people — new rate and estimation counts, crosschecks, active days and entries per active day.`,
    },
    {
      kind: "trend", title: "Progress over time",
      body: `${fnum(trend.length)} snapshot${trend.length === 1 ? "" : "s"} — completion, received, priced and estimated counts for every Masterfile uploaded.`,
    },
  ];

  return (
    <div className="sec">
      <div className="sec-head">
        <span className="eyebrow">Section 10</span>
        <h2><i className="bi bi-download hico" aria-hidden="true" />Export</h2>
        <span className="note">Everything below respects the filters at the top of the page</span>
      </div>

      <div className="exportgrid">
        {cards.map((c) => (
          <div className="exportcard" key={c.kind}>
            <h3><i className={`bi ${ICON[c.kind]} hico`} aria-hidden="true" />{c.title}</h3>
            <p>{c.body}</p>
            <div className="acts">
              <button className="btn btn--primary" type="button" onClick={() => doCsv(c.kind)}>
                Download CSV
              </button>
              <button className="btn" type="button" onClick={() => void doCopy(c.kind)}>
                Copy for Excel
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="card card-pad" style={{ marginTop: 12 }}>
        <h3 style={{ fontSize: 13 }}>Preview / manual copy</h3>
        <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--ink-2)" }}>
          <b>Download CSV</b> writes the file straight to your downloads folder.{" "}
          <b>Copy for Excel</b> puts the same rows on the clipboard tab-separated, ready to paste
          straight into a sheet &mdash; useful if a save is ever refused. The box below shows
          exactly what either button produces.
        </p>
        <textarea
          className="csvout" readOnly value={preview}
          placeholder="Choose an export above to preview it here."
        />
      </div>
    </div>
  );
}
